import React, { useEffect, useContext, useMemo, useState, useCallback } from "react";
import {
  Box,
  Divider,
  Typography,
  Grid,
  Paper,
  CardContent,
} from "@mui/material";

import { AppPermissionsContext } from "../../components/common/app-context/app-permission-context";
import FormControlComponent from "../../components/common/form-control-component/form-control-component";
import ContentComponent from "../../components/common/content-component";
import WorldMap from "../../components/map";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import {
  getEmbedConfig,
  getSourceDataDetailsById,
  getAnalyticsConsoleById,
  getAnalyticConsoleDataDetailsByCompany,
} from "../../services/api-services";

import ArrowBackIosNewTwoToneIcon from "@mui/icons-material/ArrowBackIosNewTwoTone";
import ArrowForwardIosTwoToneIcon from "@mui/icons-material/ArrowForwardIosTwoTone";
import { useParams, useNavigate } from "react-router-dom";
import * as ROUTES from "../../config/routes";
import { cbcrReportId, pillarTwoReportId } from "../../config/constants";

/* ---------- helpers ---------- */

const toTiles = (o) => [
  { title: "ZAR", value: "CURRENCY" },
  { title: o?.totalTaxJurisdictions, value: "TAX JURISDICTIONS" },
  { title: o?.totalEntities, value: "ENTITIES" },
  { title: o?.totalEmployees, value: "EMPLOYEES" },
  { title: new Intl.NumberFormat().format(o?.totalGroupRevenue), value: "GROUP REVENUE" },
  { title: new Intl.NumberFormat().format(o?.totalGroupProfit), value: "GROUP PROFIT" },
  { title: o?.significantActivity, value: "SIGNIFICANT ACTIVITY" },
];

const mergeMapData = (rows = []) => {
  const byId = new Map();
  for (const r of rows) {
    const curr = byId.get(r.id) ?? { id: r.id, value: 0, capital: 0, employees: 0 };
    curr.value += r.value || 0;
    curr.capital += r.capital || 0;
    curr.employees += r.employees || 0;
    byId.set(r.id, curr);
  }
  return Array.from(byId.values());
};

/* ---------- Power BI wrapper ---------- */

const AnalyticConsolePowerBi = ({ data }) => {
  const { overview, consoleData, showReportNav } = data;
  const { storedUser } = useContext(AppPermissionsContext);

  const [embedConfig, setEmbedConfig] = useState(null);
  const [report, setReport] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  
  const pagesLoadedRef = React.useRef(false);

  const hasPrev = showReportNav && pageIndex > 0;
  const hasNext = showReportNav && pageIndex < pages.length - 1;

  useEffect(() => {
    if (!storedUser || !consoleData?.reportId) return;
    let mounted = true;
    getEmbedConfig(storedUser, consoleData.reportId)
      .then((res) => mounted && setEmbedConfig(res.data))
      .catch(console.error);
    return () => {
      mounted = false;
    };
  }, [storedUser, consoleData?.reportId]);

  useEffect(() => {
    pagesLoadedRef.current = false;
    setPages([]);
    setPageIndex(0);
  }, [embedConfig?.id]);

  const handleRendered = useCallback(async (event) => {
    
    if (pagesLoadedRef.current) {
      return;
    }
    
    const reportObj = window.report || event?.detail;
    
    if (!reportObj) {
      return;
    }

    try {
      const p = await reportObj.getPages();
      
      
      if (p && p.length > 0) {
        setPages(p);
        setPageIndex(0);
        await p[0].setActive();
        pagesLoadedRef.current = true;
      } else {
        console.warn("No pages returned from getPages()");
      }
    } catch (error) {
      console.error("Error loading pages:", error);
    }
  }, []);

  const goPrev = useCallback(async () => {
    if (!hasPrev || !pages[pageIndex - 1]) return;
    try {
      const nextIndex = pageIndex - 1;
      await pages[nextIndex].setActive();
      setPageIndex(nextIndex);
    } catch (error) {
      console.error("Error navigating to previous page:", error);
    }
  }, [hasPrev, pageIndex, pages]);

  const goNext = useCallback(async () => {
    if (!hasNext || !pages[pageIndex + 1]) return;
    try {
      const nextIndex = pageIndex + 1;
      await pages[nextIndex].setActive();
      setPageIndex(nextIndex);
    } catch (error) {
      console.error("Error navigating to next page:", error);
    }
  }, [hasNext, pageIndex, pages]);

  if (!embedConfig) return null;
  
  return (
  <Paper
    elevation={1}
    sx={{
      backgroundColor: 'rgb(255, 255, 255)',
      borderRadius: '10px',
      boxShadow: 'rgb(159 162 191 / 18%) 0px 9px 16px, rgb(159 162 191 / 32%) 0px 2px 2px',
      padding: '2rem',
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      minHeight: '600px',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}
  >
    {hasPrev && (
      <div 
        className="prev-next" 
        onClick={goPrev}
        style={{ 
          position: 'absolute',
          left: '36px',
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          zIndex: 1000
        }}
      >
        <ArrowBackIosNewTwoToneIcon
          sx={{
            fontSize: "2.2em",
            "&:hover": { transform: "scale(1.2)" },
            cursor: 'pointer'
          }}
        />
      </div>
    )}

    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <PowerBIEmbed
        embedConfig={{
          type: "report",
          id: embedConfig.id,
          embedUrl: embedConfig.embedUrl,
          accessToken: embedConfig.token,
          permissions: models.Permissions.All,
          tokenType: models.TokenType.Embed,
          viewMode: models.ViewMode.View,
          filters: consoleData?.client?.name ? [
            {
              $schema: "http://powerbi.com/product/schema#basic",
              target: { table: "EntityRevenues", column: "MNE Group Name" },
              operator: "In",
              values: [consoleData.client.name],
              filterType: models.FilterType.Basic,
            }
          ] : [],
          settings: {
            panes: {
              filters: { expanded: false, visible: false },
              pageNavigation: { visible: false },
            },
            background: showReportNav
              ? models.BackgroundType.Transparent
              : models.BackgroundType.Default,
          },
        }}
        eventHandlers={
          new Map([
            ["loaded", () => console.log("Report loaded event fired")],
            ["rendered", handleRendered],
            ["error", (event, embed) => console.warn({ event, embed })],
          ])
        }
        cssClassName="pbi-container"
        getEmbeddedComponent={(embeddedReport) => {
          console.log("getEmbeddedComponent called, setting report");
          window.report = embeddedReport;
          setReport(embeddedReport);
        }}
      />
    </Box>

    {hasNext && (
      <div 
        className="prev-next" 
        onClick={goNext} 
        style={{ 
          position: 'absolute',
          right: "36px",
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          zIndex: 1000
        }}
      >
        <ArrowForwardIosTwoToneIcon
          sx={{
            fontSize: "2.2em",
            "&:hover": { transform: "scale(1.2)" },
            cursor: 'pointer'
          }}
        />
      </div>
    )}
  </Paper>
);
};

/* ---------- CBCR / Pillar Two card ---------- */

const AnalyticCardContent = ({ data }) => {
  const { templateId, consoleId } = data;
  const { storedUser, overview } = useContext(AppPermissionsContext);

  const [consoleData, setConsoleData] = useState(null);
  const [overviewData, setOverviewData] = useState(null);

  const tiles = useMemo(() => toTiles(overviewData || {}), [overviewData]);
  const mapData = useMemo(() => mergeMapData(overviewData?.mapData), [overviewData]);

  useEffect(() => {
    if (!storedUser || !consoleId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getAnalyticsConsoleById(storedUser, consoleId);
        if (!mounted) return;
        setConsoleData(res.data);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [storedUser, consoleId]);

  useEffect(() => {
    if (!storedUser || !consoleData || templateId !== cbcrReportId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getSourceDataDetailsById(storedUser, consoleData.file);
        if (!mounted) return;
        setOverviewData(res.data);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [storedUser, consoleData, templateId]);

  if (!consoleData) return null;

  if (templateId === cbcrReportId) {
    return (
      <>
        {overview ? (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgb(255, 255, 255)',
              borderRadius: '10px',
              boxShadow: 'rgb(159 162 191 / 18%) 0px 9px 16px, rgb(159 162 191 / 32%) 0px 2px 2px',
              padding: '2rem',
              marginBottom: '2rem'
            }}
          >
            <CardContent className="card-content-component">
              <Box className="analytic-no-period analytic-space">
                {tiles.map((t, i) => (
                  <Grid key={`${t.value}-${i}`} item xs={12} lg={3}>
                    <Paper className="analytic-no-period-content" elevation={0} variant="outlined">
                      <Box className="analytic-no-period-content-position">
                        <Typography sx={{ m: 0, fontWeight: 700, lineHeight: 1.167, fontSize: "1rem" }}>
                          {t.title}
                        </Typography>

                        <Divider
                          sx={{
                            border: 0,
                            width: 50,
                            background: "rgb(255 93 0)",
                            height: 6,
                            borderRadius: "50px",
                            my: "18px",
                            mx: "auto",
                          }}
                        />

                        <Typography
                          sx={{
                            m: 0,
                            lineHeight: 1.4,
                            color: "rgb(111 115 123)",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            textTransform: "uppercase",
                          }}
                        >
                          {t.value}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Box>
            </CardContent>

            {overviewData && <WorldMap chartID="world-map" data={mapData} />}
          </Paper>
        ) : (
          <AnalyticConsolePowerBi data={{ overview, consoleData, showReportNav: true }} />
        )}
      </>
    );
  }

  if (templateId === pillarTwoReportId) {
    return <AnalyticConsolePowerBi data={{ overview, consoleData, showReportNav: false }} />;
  }

  return null;
};

/* ---------- container ---------- */

const AnalyticConsoleShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { storedUser } = useContext(AppPermissionsContext);

  const [templateId, setTemplateId] = useState(null);
  const [yearChoice, setYearChoice] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  useEffect(() => {
    if (!storedUser || !id) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getAnalyticConsoleDataDetailsByCompany(storedUser, id);
        if (!mounted) return;

        const consoles = res.data.map((f) => ({
          id: String(f.id),
          templateId: f.template,
          name: String(f.year),
          year: f.year,
          company: f.companyId,
        }));

        const current = consoles.find((c) => c.id === String(id));
        setTemplateId(current?.templateId ?? null);

        setYearOptions(
          consoles
            .filter(f => f.templateId === String(current?.templateId))
            .sort((a, b) => String(b.name).localeCompare(String(a.name)))
            .map((c) => ({ id: c.id, name: c.name }))
        );
        setYearChoice({ id: String(id), name: current?.name ?? "Select Year" });
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [storedUser, id]);

  useEffect(() => {
    if (!yearChoice?.id || String(yearChoice.id) === String(id)) return;
    navigate(`${ROUTES.ANALYTIC_CONSOLES}/show/${yearChoice.id}`);
  }, [yearChoice, id, navigate]);

  return (
    <ContentComponent
      className="analystic-console-component"
      data={{
        title: "Analytics Console",
        description: "",
        component: 
          <FormControlComponent
            data={{
              defaultValue: "Select Year",
              label: "Select Year",
              name: "id",
              type: "selectbox",
              sx: { width: "15rem" },
              formData: yearChoice,
              setFormData: setYearChoice,
              options: yearOptions,
            }}
          />
      }}
    >
      <AnalyticCardContent data={{ templateId, consoleId: id }} />
    </ContentComponent>
  );
};

export default AnalyticConsoleShow;